import { useState } from 'react'
import { CheckCircle2, Info } from 'lucide-react'
import { useParams } from 'react-router'
import { ErrorState, EmptyState, PageHeader, StatusBadge } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useAppraisalCycles, useAppraisalMutations, useCycleAppraisals } from '../api'
import { APPRAISAL_STATUS_LABELS, REVIEW_TYPE_LABELS } from '../types'
import type { AppraisalOutcomeView, AppraisalStatus, ReviewType } from '../types'

const STATUS_TONE: Record<AppraisalStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  DRAFT: 'neutral',
  SELF_REVIEW: 'warning',
  MANAGER_REVIEW: 'info',
  CALIBRATION: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'neutral',
}

/**
 * Everybody in one cycle.
 *
 * Two things this screen is careful about: an appraisal with nothing submitted shows no score
 * rather than a zero, and completing one reports exactly what it sent to training and to
 * compensation — including when it sent nothing, and why.
 */
export function AppraisalCycleDetailPage() {
  const { id } = useParams<{ id: string }>()
  const cycles = useAppraisalCycles()
  const appraisals = useCycleAppraisals(id ?? null)
  const cycle = cycles.data?.find((row) => row.id === id)
  useBreadcrumb(cycle?.name ?? 'Cycle')
  const { complete } = useAppraisalMutations()

  const [outcome, setOutcome] = useState<AppraisalOutcomeView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  // An annual cycle covers everybody in the entity — on this dev database that is 1,248
  // people. Rendering them all froze the page, so the table shows a bounded window and says
  // how many it is not showing rather than silently truncating.
  const RENDER_LIMIT = 100

  if (appraisals.isPending) return <Skeleton className="h-64" />
  if (appraisals.isError) {
    return <ErrorState error={appraisals.error} onRetry={() => void appraisals.refetch()} />
  }

  const matching = appraisals.data.filter((appraisal) => {
    if (search.trim() === '') return true
    const needle = search.trim().toLowerCase()
    return (appraisal.employeeName ?? '').toLowerCase().includes(needle)
      || (appraisal.employeeCode ?? '').toLowerCase().includes(needle)
  })
  const visible = matching.slice(0, RENDER_LIMIT)

  const act = (work: () => Promise<unknown>, fallback: string) => {
    void (async () => {
      setError(null)
      try {
        await work()
      } catch (caught) {
        setError(toApiProblem(caught)?.detail ?? fallback)
      }
    })()
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={cycle?.name ?? 'Cycle'}
        subtitle={cycle ? `${cycle.periodStart} → ${cycle.periodEnd} · ${cycle.templateName ?? ''}` : ''}
      />

      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      {outcome && (
        <Card className="space-y-2 border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
          <p className="flex items-start gap-2 font-medium">
            <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            Appraisal completed
          </p>
          {outcome.trainingNote && <p>{outcome.trainingNote}</p>}
          {outcome.compensationNote && <p>{outcome.compensationNote}</p>}
        </Card>
      )}

      {appraisals.data.length > 0 && (
        <Card className="p-4">
          <Label htmlFor="cycle-search">Find somebody</Label>
          <Input
            id="cycle-search"
            className="mt-1 max-w-md"
            placeholder="Name or employee code"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </Card>
      )}

      {appraisals.data.length === 0 ? (
        <EmptyState
          title="Nobody is being appraised in this cycle"
          description="Open the cycle to create an appraisal for each person, with the reviewer passes its template expects."
        />
      ) : (
        <Card>
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Person</th>
                  <th className="p-3 text-start font-medium">Manager</th>
                  <th className="p-3 text-start font-medium">Reviews</th>
                  <th className="p-3 text-end font-medium">Score</th>
                  <th className="p-3 text-start font-medium">Status</th>
                  <th className="p-3 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {visible.map((appraisal) => (
                  <tr key={appraisal.id} className="border-b border-slate-100 align-top">
                    <td className="p-3">
                      {appraisal.employeeName ?? appraisal.employeeCode}
                      <span className="block font-mono text-xs text-slate-600">
                        {appraisal.reference}
                      </span>
                    </td>
                    <td className="p-3 text-slate-600">{appraisal.managerName ?? '—'}</td>
                    <td className="p-3">
                      <ul className="space-y-0.5 text-xs text-slate-600">
                        {appraisal.reviews.map((review) => (
                          <li key={review.id}>
                            {REVIEW_TYPE_LABELS[review.reviewType as ReviewType]
                              ?? review.reviewType}
                            : {review.submitted ? 'submitted' : review.progress}
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td className="p-3 text-end">
                      {appraisal.finalScore == null ? (
                        <span className="text-xs text-slate-600">Not scored</span>
                      ) : (
                        <>
                          <span className={appraisal.calibratedScore == null
                            ? 'font-medium' : 'text-slate-500 line-through'}>
                            {appraisal.finalScore}
                          </span>
                          {appraisal.calibratedScore != null && (
                            <span className="ms-2 font-medium">
                              {appraisal.calibratedScore}
                            </span>
                          )}
                        </>
                      )}
                    </td>
                    <td className="p-3">
                      <StatusBadge
                        status={appraisal.status}
                        tone={STATUS_TONE[appraisal.status as AppraisalStatus] ?? 'neutral'}
                        label={APPRAISAL_STATUS_LABELS[appraisal.status as AppraisalStatus]
                          ?? appraisal.status}
                      />
                      {appraisal.trainingNeedsCreated != null && (
                        <p className="mt-1 text-xs text-slate-600">
                          {appraisal.trainingNeedsCreated} training need(s) raised
                        </p>
                      )}
                    </td>
                    <td className="p-3 text-end">
                      {appraisal.status !== 'COMPLETED' && appraisal.status !== 'CANCELLED' && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => act(async () => {
                            const result = await complete.mutateAsync({ id: appraisal.id })
                            setOutcome(result)
                          }, 'That appraisal could not be completed')}
                        >
                          Complete
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {matching.length > visible.length && (
        <p className="text-sm text-slate-600">
          Showing {visible.length} of {matching.length} appraisals. Search above to find
          somebody — the rest are not hidden, only unrendered.
        </p>
      )}

      <p className="flex items-start gap-2 text-sm text-slate-600">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        An appraisal with nothing submitted shows "Not scored", not zero — an unrated appraisal
        and a bad one are different facts. Completing one raises training needs from the
        ratings below the scale's gap threshold, and a salary revision in draft with no amount:
        this system ships no rating-to-increment matrix.
      </p>
    </div>
  )
}
